import { useCallback, useEffect, useMemo } from 'react'
import { createRealtimeCacheUpdater } from '@/lib/realtime/RealtimeCacheUpdater'
import { tableEditingStore } from '@/store/table-editing.store'
import { useQueryClient } from '@tanstack/react-query'

import type { Task, TaskWithAssignments } from '@scrambl/shared'

type PendingRemoteUpdate = {
  value: unknown
  timestamp: number
}

type TaskRecord = Record<string, unknown>
type TaskUpdateApplier = (
  taskId: string,
  updates: Partial<Task>,
  force?: boolean,
) => void

const editingTasks = new Set<string>()
const editingFields = new Map<string, Set<string>>()
const pendingRemoteUpdates = new Map<string, Map<string, PendingRemoteUpdate>>()
const lastFieldAppliedMap = new Map<string, Map<string, number>>()

let deferredUpdatesSubscriptionCount = 0
let deferredUpdatesCleanup: (() => void) | null = null
let latestDeferredUpdateApplier: TaskUpdateApplier | null = null

const stripUndefinedValues = (value: TaskRecord): Partial<TaskRecord> => {
  const next: Partial<TaskRecord> = {}
  Object.entries(value).forEach(([key, itemValue]) => {
    if (itemValue !== undefined) {
      next[key] = itemValue
    }
  })
  return next
}

const preserveAvailableStatuses = (
  cachedTask: TaskWithAssignments | undefined,
  updates: Partial<TaskRecord>,
): TaskRecord => {
  if (!cachedTask) {
    return updates as TaskRecord
  }

  if (
    (!Object.prototype.hasOwnProperty.call(updates, 'availableStatuses') ||
      updates.availableStatuses == null) &&
    Object.prototype.hasOwnProperty.call(cachedTask, 'availableStatuses')
  ) {
    return {
      ...updates,
      availableStatuses: (cachedTask as TaskRecord).availableStatuses,
    } as TaskRecord
  }

  return updates as TaskRecord
}

function getOrCreateEditingFieldSet(taskId: string) {
  let set = editingFields.get(taskId)
  if (!set) {
    set = new Set<string>()
    editingFields.set(taskId, set)
  }
  return set
}

function getOrCreatePendingUpdates(taskId: string) {
  let pendingByField = pendingRemoteUpdates.get(taskId)
  if (!pendingByField) {
    pendingByField = new Map<string, PendingRemoteUpdate>()
    pendingRemoteUpdates.set(taskId, pendingByField)
  }
  return pendingByField
}

function clearPendingUpdate(taskId: string, field: string) {
  const pendingByField = pendingRemoteUpdates.get(taskId)
  const pending = pendingByField?.get(field) || null

  if (pendingByField) {
    pendingByField.delete(field)
    if (pendingByField.size === 0) {
      pendingRemoteUpdates.delete(taskId)
    }
  }

  return pending
}

function retainRecentTaskTimestamps(
  fieldMap: Map<string, number>,
  now: number,
) {
  const maxAge = 60000
  for (const [field, timestamp] of fieldMap.entries()) {
    if (now - timestamp > maxAge) {
      fieldMap.delete(field)
    }
  }
}

const cleanupEditingState = () => {
  const now = Date.now()

  for (const [taskId, fieldMap] of lastFieldAppliedMap.entries()) {
    retainRecentTaskTimestamps(fieldMap, now)
    if (fieldMap.size === 0) {
      lastFieldAppliedMap.delete(taskId)
    }
  }

  for (const [taskId, pendingByField] of pendingRemoteUpdates.entries()) {
    for (const [field, pending] of pendingByField.entries()) {
      if (now - pending.timestamp > 60000) {
        pendingByField.delete(field)
      }
    }
    if (pendingByField.size === 0) {
      pendingRemoteUpdates.delete(taskId)
    }
  }
}

const ensureDeferredUpdatesBridge = () => {
  if (deferredUpdatesCleanup) {
    return
  }

  // Replay deferred task updates PER ROW as soon as that row's editors close
  // (matching realtime-guard's flushDeferredRowUpdates for other entities).
  // Updates are queued per-row, so waiting for ALL popovers to close would
  // strand a row's update whenever another editor overlaps.
  let flushScheduled = false
  const tryFlush = () => {
    flushScheduled = false
    if (!latestDeferredUpdateApplier) return
    const state = tableEditingStore.getState()
    const deferredUpdates = state.getDeferredUpdatesByType('task')
    for (const [rowId, deferred] of deferredUpdates) {
      if (state.hasOpenPopoverForRow(rowId)) continue
      state.clearDeferredUpdate(rowId)
      if (deferred) {
        latestDeferredUpdateApplier(
          rowId,
          deferred.updates as Partial<Task>,
          true,
        )
      }
    }
  }

  const unsubscribe = tableEditingStore.subscribe(state => {
    if (flushScheduled || state.deferredUpdates.size === 0) return
    flushScheduled = true
    // Microtask avoids mutating the store inside its own notification
    queueMicrotask(tryFlush)
  })

  // Recover updates queued while no bridge was mounted
  if (tableEditingStore.getState().deferredUpdates.size > 0) {
    flushScheduled = true
    queueMicrotask(tryFlush)
  }

  const cleanupInterval = window.setInterval(() => {
    tableEditingStore.getState().cleanupStaleUpdates()
    cleanupEditingState()
  }, 30000)

  deferredUpdatesCleanup = () => {
    unsubscribe()
    window.clearInterval(cleanupInterval)
    deferredUpdatesCleanup = null
  }
}

export function markTaskEditing(taskId: string, isEditing: boolean) {
  if (isEditing) {
    editingTasks.add(taskId)
  } else {
    editingTasks.delete(taskId)
  }
}

export function isTaskBeingEdited(taskId: string) {
  return editingTasks.has(taskId)
}

export function getEditingFields(taskId: string) {
  return editingFields.get(taskId) || new Set<string>()
}

export function beginFieldInteraction(taskId: string, field: string) {
  getOrCreateEditingFieldSet(taskId).add(field)
  editingTasks.add(taskId)
}

export function endFieldInteraction(taskId: string, field: string) {
  const set = editingFields.get(taskId)
  if (set) {
    set.delete(field)
    if (set.size === 0) {
      editingFields.delete(taskId)
    }
  }

  if ((editingFields.get(taskId)?.size || 0) === 0) {
    editingTasks.delete(taskId)
  }

  return clearPendingUpdate(taskId, field)
}

export function getAndClearPendingUpdate(taskId: string, field: string) {
  return clearPendingUpdate(taskId, field)
}

export function queuePendingRemoteUpdate(
  taskId: string,
  field: string,
  value: unknown,
) {
  getOrCreatePendingUpdates(taskId).set(field, {
    value,
    timestamp: Date.now(),
  })
}

export function getLastFieldApplied(taskId: string, field: string) {
  return lastFieldAppliedMap.get(taskId)?.get(field) || 0
}

export function recordAppliedFields(taskId: string, fields: string[]) {
  let fieldMap = lastFieldAppliedMap.get(taskId)
  if (!fieldMap) {
    fieldMap = new Map<string, number>()
    lastFieldAppliedMap.set(taskId, fieldMap)
  }

  const now = Date.now()
  fields.forEach(field => fieldMap!.set(field, now))
}

export function useTaskRealtimeEditing() {
  return {
    markTaskEditing,
    isTaskBeingEdited,
    beginFieldInteraction,
    endFieldInteraction,
    getAndClearPendingUpdate,
    applyPendingNow: (taskId: string, field: string) => {
      const pending = getAndClearPendingUpdate(taskId, field)
      if (!pending || !latestDeferredUpdateApplier) return false

      latestDeferredUpdateApplier(
        taskId,
        { [field]: pending.value } as Partial<Task>,
        true,
      )
      return true
    },
  }
}

export function useTaskRealtimeEditingSync() {
  const queryClient = useQueryClient()
  // The updater owns the short-lived batching queue. Reusing it for the
  // lifetime of this QueryClient lets updates received across React renders
  // coalesce into the same flush instead of stranding work on old instances.
  const updater = useMemo(
    () => createRealtimeCacheUpdater(queryClient, 120),
    [queryClient],
  )

  const applyTaskUpdates = useCallback<TaskUpdateApplier>(
    (taskId, updates, force = false) => {
      if (!force && isTaskBeingEdited(taskId)) {
        return
      }

      // Defer only when THIS task's row has an open editor — patches to other
      // rows preserve row identity and can't disrupt open popovers elsewhere.
      const rowHasOpenEditor = tableEditingStore
        .getState()
        .hasOpenPopoverForRow(taskId)
      if (rowHasOpenEditor && !force) {
        tableEditingStore
          .getState()
          .queueDeferredUpdate(
            'task',
            taskId,
            updates as Record<string, unknown>,
          )
        return
      }

      const lockedFields = getEditingFields(taskId)
      const applyNow: Partial<Task> = {}

      Object.entries(updates).forEach(([field, value]) => {
        if (value === undefined) {
          return
        }

        if (!force && lockedFields.has(field)) {
          queuePendingRemoteUpdate(taskId, field, value)
        } else {
          const mutableApplyNow = applyNow as Record<string, unknown>
          mutableApplyNow[field] = value
        }
      })

      if (Object.keys(applyNow).length === 0) {
        return
      }

      const cachedTask = queryClient.getQueryData<TaskWithAssignments>([
        'task',
        taskId,
      ])
      const normalizedApplyNow = preserveAvailableStatuses(
        cachedTask,
        stripUndefinedValues(applyNow as TaskRecord),
      ) as Partial<TaskWithAssignments>

      if (Object.keys(normalizedApplyNow).length === 0) {
        return
      }

      updater.updateTaskFields(taskId, normalizedApplyNow)
      recordAppliedFields(taskId, Object.keys(applyNow))
      updater.invalidate(['task', taskId], 'none')
    },
    [queryClient, updater],
  )

  useEffect(() => {
    latestDeferredUpdateApplier = applyTaskUpdates
    deferredUpdatesSubscriptionCount += 1
    ensureDeferredUpdatesBridge()

    return () => {
      deferredUpdatesSubscriptionCount -= 1
      if (deferredUpdatesSubscriptionCount <= 0) {
        deferredUpdatesCleanup?.()
        latestDeferredUpdateApplier = null
        deferredUpdatesSubscriptionCount = 0
      }
    }
  }, [applyTaskUpdates])

  return {
    applyTaskUpdates,
    applyPendingNow: (taskId: string, field: string) => {
      const pending = getAndClearPendingUpdate(taskId, field)
      if (!pending) return false

      applyTaskUpdates(
        taskId,
        { [field]: pending.value } as Partial<Task>,
        true,
      )
      return true
    },
  }
}
